import React from 'react';
import type { IntlShape } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import CreateIcon from './whiteboard-icon.svg';
import { messages } from './messages.ts';
import { createWhiteboardQuickInsertModule } from './create-whiteboard-quick-insert.tsx';
import { legacySlashCommands, slashCommands } from './slash-commands.tsx';

type CreateWhiteboardExtensionProps = {
	pageId: string;
	spaceId: string;
	spaceKey: string;
	intl: IntlShape;
	experienceTracker?: ExperienceTrackerAPI;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	flags?: FlagsStateContainer;
	contentType?: string;
	allowWhiteboardQuickInsert?: boolean;
	isCreationQuickInsertHidden?: () => boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

export const createWhiteboardExtension = ({
	pageId,
	spaceId,
	spaceKey,
	intl,
	flags,
	experienceTracker,
	createAnalyticsEvent,
	contentType,
	allowWhiteboardQuickInsert,
	isCreationQuickInsertHidden,
	pageTreeStateUpdater,
}: CreateWhiteboardExtensionProps): ExtensionManifest => {
	// empty extension if not on page
	if (contentType !== 'page') {
		return {
			title: intl.formatMessage(messages.whiteboardManifestTitle),
			description: intl.formatMessage(messages.whiteboardManifestDescription),
			type: 'com.atlassian.confluence.macro.core',
			key: 'whiteboard-extension',
			icons: {
				// eslint-disable-next-line import/dynamic-import-chunkname
				'48': () => import('@atlaskit/icon-lab/core/browser-extension'),
			},
			modules: {
				quickInsert: [],
			},
		};
	}

	const icon = async () => () => <img src={CreateIcon} alt="" />;

	const quickInsertCommands: ExtensionModule[] = (
		isExperimentEnabled('platform_editor_slash_command') ? slashCommands : legacySlashCommands
	).map((command) => ({
		key: `create-${command.key}`,
		title: intl.formatMessage(command.title),
		description: intl.formatMessage(command.description),
		preview: {
			...command.preview,
			attribution: {
				name: intl.formatMessage(quickInsertMessages.previewAttributionConfluence),
				icon: ConfluenceAttributionIcon,
			},
		},
		...(isExperimentEnabled('platform_editor_slash_command') ? { category: 'create' } : {}),
		priority: command.priority,
		...(isCreationQuickInsertHidden ? { isHidden: isCreationQuickInsertHidden } : {}),
		icon,
		action: createWhiteboardQuickInsertModule(
			pageId,
			spaceId,
			spaceKey,
			experienceTracker,
			createAnalyticsEvent,
			flags,
			command.templateKey,
			pageTreeStateUpdater,
		),
		...(command.key === 'diagram' && {
			featured: true,
		}),
	}));

	return {
		title: intl.formatMessage(messages.whiteboardManifestTitle),
		description: intl.formatMessage(messages.whiteboardManifestDescription),
		type: 'com.atlassian.confluence.macro.core',
		key: 'whiteboard-extension',
		icons: { '48': icon },
		modules: {
			quickInsert: allowWhiteboardQuickInsert ? quickInsertCommands : [],
		},
	};
};
